import type {
  DownloadAuth,
  ModelDetail,
  ModelItem,
  ModelListParams,
  ModelOrder,
  PaymentResult,
  RelatedModel,
} from '@/types/model'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  createModelOrder,
  generateModelIdempotencyKey,
  getModelDetail,
  getModelDownloadUrl,
  getModelList,
  getRelatedModels,
  payModelOrder,
} from '@/api/model/index'
import { addFavorite, removeFavorite } from '@/api/userCenter/favorites'
import { useUserStorev2 } from '@/store/modules/userCenter/userStore'

export const useModelStore = defineStore('model-v2', () => {
  // State
  const list = ref<ModelItem[]>([])
  const total = ref(0)
  const queryParams = ref<ModelListParams>({
    page: 1,
    pageSize: 20,
    sort: 'recommend',
  })

  const detail = ref<ModelDetail | null>(null)
  const related = ref<RelatedModel[]>([])

  const loading = ref({
    list: false,
    detail: false,
    related: false,
    payment: false,
    download: false,
  })

  const error = ref({
    list: null as string | null,
    detail: null as string | null,
    payment: null as string | null,
    download: null as string | null,
  })

  const currentOrder = ref<ModelOrder | null>(null)
  const paymentResult = ref<PaymentResult | null>(null)
  const downloadAuth = ref<DownloadAuth | null>(null)

  // Local cache for purchased status to avoid refetching
  const purchasedMap = ref<Record<string, boolean>>({})

  // 收藏状态缓存 - 用于详情页与列表页状态同步
  const favoritesMap = ref<Record<string, boolean>>({})

  // Getters
  const purchaseStatus = computed(() => {
    if (!detail.value)
      return 'notPurchased'

    // P1-01: 作者本人直接可下载 - 前端主动校验
    // 优先使用后端返回的isOwner字段
    if (detail.value.isOwner) {
      return 'owner'
    }
    // 前端防御性校验：比对author.id与当前用户ID
    const userStore = useUserStorev2()
    if (userStore.userInfo?.id && detail.value.author?.id === userStore.userInfo.id) {
      return 'owner'
    }

    if (detail.value.pricing.isFree)
      return 'free'
    if (purchasedMap.value[detail.value.id] || detail.value.purchaseStatus === 'purchased') {
      return 'purchased'
    }
    return 'notPurchased'
  })

  const canDownload = computed(() => {
    if (purchaseStatus.value === 'owner')
      return true
    if (purchaseStatus.value === 'free')
      return true
    if (purchaseStatus.value === 'purchased')
      return true
    return false
  })

  // Actions
  const fetchList = async (params: ModelListParams) => {
    loading.value.list = true
    error.value.list = null
    // Merge params with existing state if needed, or just overwrite
    queryParams.value = { ...queryParams.value, ...params }

    try {
      const res = await getModelList(queryParams.value)
      list.value = res.list
      total.value = res.total
      return res
    }
    catch (err: unknown) {
      error.value.list = err instanceof Error ? err.message : 'Failed to load model list'
      throw err
    }
    finally {
      loading.value.list = false
    }
  }

  const loadDetail = async (id: string | number) => {
    loading.value.detail = true
    error.value.detail = null
    try {
      const res = await getModelDetail(id)
      detail.value = res
      // Sync purchased status to map
      if (res.purchaseStatus === 'purchased') {
        purchasedMap.value[res.id] = true
      }
      // P1-03: Sync favorite status from server
      if (res.isFavorited !== undefined) {
        favoritesMap.value[String(res.id)] = res.isFavorited
      }
    }
    catch (err: unknown) {
      error.value.detail = err instanceof Error ? err.message : 'Failed to load model detail'
    }
    finally {
      loading.value.detail = false
    }
  }

  const loadRelated = async (id: string | number) => {
    loading.value.related = true
    try {
      const res = await getRelatedModels(id)
      related.value = res
    }
    catch {
      // 忽略错误，已通过 error.value 管理状态
    }
    finally {
      loading.value.related = false
    }
  }

  const createOrder = async (id: string | number) => {
    loading.value.payment = true
    error.value.payment = null
    try {
      // P0-Fix: 生成幂等性Key防止重复下单
      const userStore = useUserStorev2()
      const userId = userStore.userInfo?.id || 'anonymous'
      const idempotencyKey = generateModelIdempotencyKey('model_order', userId, id)
      const res = await createModelOrder(id, { method: 'points', idempotencyKey })
      currentOrder.value = res
      return res
    }
    catch (err: unknown) {
      error.value.payment = err instanceof Error ? err.message : 'Failed to create order'
      throw err
    }
    finally {
      loading.value.payment = false
    }
  }

  const payOrder = async (orderId: string, forceFail = false) => {
    loading.value.payment = true
    error.value.payment = null
    try {
      // P0-Fix: 生成幂等性Key防止重复支付
      const userStore = useUserStorev2()
      const userId = userStore.userInfo?.id || 'anonymous'
      const idempotencyKey = generateModelIdempotencyKey('model_pay', userId, orderId)
      const res = await payModelOrder(orderId, {
        method: 'points',
        forceFail,
        idempotencyKey,
      })
      paymentResult.value = res

      if (res.status === 'success' && detail.value) {
        // Update local state
        purchasedMap.value[detail.value.id] = true
        detail.value.purchaseStatus = 'purchased'

        // P0-Fix: 更新下载权限状态，允许立即下载
        if (!detail.value.downloadAuth) {
          detail.value.downloadAuth = { canDownload: true }
        }
        else {
          detail.value.downloadAuth.canDownload = true
          detail.value.downloadAuth.reason = undefined
        }
      }
      return res
    }
    catch (err: unknown) {
      error.value.payment = err instanceof Error ? err.message : 'Payment failed'
      throw err
    }
    finally {
      loading.value.payment = false
    }
  }

  const fetchDownloadUrl = async (id: string | number) => {
    loading.value.download = true
    error.value.download = null
    try {
      const res = await getModelDownloadUrl(id)
      downloadAuth.value = res
      return res
    }
    catch (err: unknown) {
      error.value.download = err instanceof Error ? err.message : 'Failed to get download URL'
      throw err
    }
    finally {
      loading.value.download = false
    }
  }

  const resetPayment = () => {
    currentOrder.value = null
    paymentResult.value = null
    error.value.payment = null
  }

  const resetState = () => {
    list.value = []
    detail.value = null
    related.value = []
    currentOrder.value = null
    paymentResult.value = null
    downloadAuth.value = null
    favoritesMap.value = {}
    error.value = { list: null, detail: null, payment: null, download: null }
  }

  // 收藏状态管理
  const isFavorited = (modelId: string) => {
    return favoritesMap.value[modelId] ?? false
  }

  const toggleFavorite = async (modelId: string) => {
    const current = favoritesMap.value[modelId] ?? false
    const newStatus = !current

    // 先乐观更新UI
    favoritesMap.value[modelId] = newStatus

    try {
      // P1-03: 调用API同步到后端
      if (newStatus) {
        await addFavorite(modelId)
      }
      else {
        await removeFavorite(modelId)
      }
      return newStatus
    }
    catch (error: unknown) {
      // 失败时回滚状态
      favoritesMap.value[modelId] = current
      throw error
    }
  }

  const setFavoriteStatus = (modelId: string, status: boolean) => {
    favoritesMap.value[modelId] = status
  }

  return {
    list,
    total,
    queryParams,
    detail,
    related,
    loading,
    error,
    currentOrder,
    paymentResult,
    downloadAuth,
    purchaseStatus,
    canDownload,
    favoritesMap,

    fetchList,
    loadDetail,
    loadRelated,
    createOrder,
    payOrder,
    fetchDownloadUrl,
    resetPayment,
    resetState,
    isFavorited,
    toggleFavorite,
    setFavoriteStatus,
  }
})

export type ModelState = ReturnType<typeof useModelStore>
